import { LegalLayout, H2, P, UL, Mail, COMPANY, PRODUCT } from "@/components/LegalLayout";

export default function TermsOfService() {
    return (
        <LegalLayout eyebrow="Legal" title="Terms of Service" lastUpdated="June 2026" testid="terms-of-service-page">
            <P>
                These Terms of Service (“Terms”) govern your access to and use of {PRODUCT} (the “Service”),
                operated by {COMPANY} (“we”, “us”, “our”). By creating an account, accessing, or using the
                Service, you (“you”, the “Customer”) agree to these Terms. If you use the Service on behalf of
                an organization, you represent that you are authorized to bind that organization.
            </P>

            <H2>The Service</H2>
            <P>
                {PRODUCT} is a cloud-based call center, softphone/CTI, and WhatsApp business messaging platform.
                Certain features rely on third-party providers (such as Twilio, Plivo, Exotel, Meta/WhatsApp,
                CRM, AI, and payment providers), some of which you connect using your own credentials.
            </P>

            <H2>Accounts & Eligibility</H2>
            <UL items={[
                "You must provide accurate account information and keep your credentials confidential.",
                "You are responsible for all activity under your workspace and for your users’ compliance with these Terms.",
                "The Service is intended for business use by users aged 18 or older.",
            ]} />

            <H2>Acceptable Use</H2>
            <P>You agree not to use the Service to:</P>
            <UL items={[
                "Send unlawful, harassing, deceptive, or unsolicited (spam) communications, or violate any applicable telemarketing, anti-spam, or data-protection laws.",
                "Violate the WhatsApp Business Messaging Policy, Meta’s platform policies, or any provider’s terms — including messaging users without a valid basis or outside permitted windows.",
                "Infringe intellectual property or privacy rights, or upload malicious code.",
                "Attempt to gain unauthorized access to the Service, other workspaces, or its infrastructure.",
            ]} />
            <P>
                You are solely responsible for obtaining any necessary consents and opt-ins from the individuals
                you contact through the Service and for honoring their opt-out requests.
            </P>

            <H2>Customer Data</H2>
            <P>
                You retain ownership of the data you and your users submit or generate through the Service
                (“Customer Data”). You grant us a limited license to process Customer Data solely to provide,
                maintain, and secure the Service. Our handling of personal data is described in our{" "}
                <a href="/privacy-policy" className="font-semibold text-brand hover:underline">Privacy Policy</a>.
            </P>

            <H2>Third-Party Services</H2>
            <P>
                The Service integrates with third-party providers. Your use of those providers is governed by
                their own terms, and we are not responsible for their availability, acts, or omissions. Charges
                billed by those providers (e.g. telephony or messaging usage) are your responsibility.
            </P>

            <H2>Fees & Billing</H2>
            <P>
                Paid plans are billed as described at sign-up or in your order. Unless stated otherwise, fees are
                non-refundable. We may change pricing prospectively with notice.
            </P>

            <H2>Service Availability</H2>
            <P>
                We aim to keep the Service available but do not guarantee uninterrupted operation. We may modify,
                suspend, or discontinue features, and may suspend access for violations of these Terms.
            </P>

            <H2>Disclaimers & Limitation of Liability</H2>
            <P>
                The Service is provided “as is” and “as available” without warranties of any kind, to the maximum
                extent permitted by law. To the extent permitted by law, {COMPANY} will not be liable for any
                indirect, incidental, special, or consequential damages, or for loss of data, profits, or
                revenue. Our aggregate liability arising out of the Service will not exceed the amounts you paid
                to us for the Service in the twelve (12) months preceding the claim.
            </P>

            <H2>Indemnification</H2>
            <P>
                You agree to indemnify and hold {COMPANY} harmless from claims arising out of your Customer Data,
                your communications sent through the Service, or your breach of these Terms or applicable law.
            </P>

            <H2>Termination</H2>
            <P>
                You may stop using the Service at any time. We may suspend or terminate access for breach of these
                Terms. Upon termination, your right to use the Service ceases; data deletion is handled per our{" "}
                <a href="/data-deletion" className="font-semibold text-brand hover:underline">Data Deletion</a> page.
            </P>

            <H2>Governing Law</H2>
            <P>
                These Terms are governed by the laws of India, without regard to conflict-of-law rules. The courts
                located in India will have exclusive jurisdiction over any disputes arising from these Terms or the
                Service.
            </P>

            <H2>Contact</H2>
            <P>For questions about these Terms, contact {COMPANY} at <Mail />.</P>
        </LegalLayout>
    );
}
